import React, { useState, useEffect, useRef } from 'react';

import { FormattedMessage } from '../../util/reactIntl';

import { Progress } from '../../components/ui/progress';

import AccountStep from './steps/AccountStep';
import CustomFieldsStep from './steps/CustomFieldsStep';
import { useWizard } from './useWizard';
import type { AccountStepValues, UserFieldConfig } from './schemas';

interface UserTypeOption {
  userType: string;
  label: string;
}

interface SignupWizardProps {
  onSubmitSignup: (params: any) => Promise<any> | void;
  signupError: any | null;
  inProgress: boolean;
  /** Validated user type (from route param, query string, or user selection). */
  userType?: string | null;
  /** All available user types from config. */
  userTypes?: UserTypeOption[];
  /** Called when user picks a type from the in-form selector. */
  onUserTypeChange?: (userType: string) => void;
  /** When true, the user type is locked from the URL and cannot be changed. */
  userTypeLocked?: boolean;
  /** Grouped custom fields — each inner array is one wizard step. */
  customFieldSteps?: UserFieldConfig[][];
  onComplete?: () => void;
}

type WizardData = AccountStepValues & Record<string, any>;

const SignupWizard: React.FC<SignupWizardProps> = ({
  onSubmitSignup,
  signupError,
  inProgress,
  userType,
  userTypes = [],
  onUserTypeChange,
  userTypeLocked = false,
  customFieldSteps = [],
  onComplete,
}) => {
  const [signupSubmitted, setSignupSubmitted] = useState(false);

  const numCustomSteps = customFieldSteps.length;
  const totalSteps = 1 + numCustomSteps; // step 0 = account, steps 1..N = custom fields
  const wizard = useWizard<WizardData>(totalSteps);

  // Track previous inProgress to detect signup completion (true → false)
  const prevInProgress = useRef(inProgress);
  useEffect(() => {
    if (signupSubmitted && prevInProgress.current && !inProgress) {
      if (!signupError) {
        onComplete?.();
      }
      // If there's a signupError, stay on the current step for retry
    }
    prevInProgress.current = inProgress;
  }, [inProgress, signupSubmitted, signupError]);

  // ── Collect all wizard data and call signup() once ──

  const submitFullSignup = (allData: Record<string, any>) => {
    const { email, password, firstName, lastName, ...rest } = allData;

    const publicData: Record<string, any> = {};
    const privateData: Record<string, any> = {};
    const protectedData: Record<string, any> = {};
    const topLevelParams: Record<string, any> = {};

    for (const [key, value] of Object.entries(rest)) {
      if (value == null) continue;
      if (key.startsWith('pub_')) {
        publicData[key.slice(4)] = value;
      } else if (key.startsWith('priv_')) {
        privateData[key.slice(5)] = value;
      } else if (key.startsWith('prot_')) {
        protectedData[key.slice(5)] = value;
      } else {
        topLevelParams[key] = value;
      }
    }

    if (userType) publicData.userType = userType;

    const params: any = {
      email,
      password,
      firstName,
      lastName,
      ...topLevelParams, // e.g. displayName
    };
    if (Object.keys(publicData).length > 0) params.publicData = publicData;
    if (Object.keys(privateData).length > 0) params.privateData = privateData;
    if (Object.keys(protectedData).length > 0) params.protectedData = protectedData;

    setSignupSubmitted(true);
    onSubmitSignup(params);
  };

  // ── Step handlers ──

  const isLastStep = wizard.currentStep === totalSteps - 1;

  const handleAccountSubmit = (data: AccountStepValues) => {
    if (numCustomSteps === 0) {
      // No custom field steps — submit signup immediately
      submitFullSignup(data);
    } else {
      wizard.goNext(data);
    }
  };

  const handleCustomFieldsSubmit = (data: Record<string, any>) => {
    if (isLastStep) {
      // Final step — merge all accumulated data and create the account
      submitFullSignup({ ...wizard.data, ...data });
    } else {
      wizard.goNext(data);
    }
  };

  const handleSkip = () => {
    if (isLastStep) {
      // Skip this step's fields but still create the account
      submitFullSignup({ ...wizard.data });
    } else {
      wizard.goNext({});
    }
  };

  const handleBack = () => {
    wizard.goBack();
  };

  // The custom field step index (0-based) for the current wizard step
  const customStepIndex = wizard.currentStep - 1;

  return (
    <div className="space-y-6">
      {totalSteps > 1 ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="SignupForm.stepProgress"
              values={{ step: wizard.currentStep + 1, totalSteps: wizard.totalSteps }}
            />
          </p>
          <Progress value={wizard.progressPercent} className="h-2" />
        </div>
      ) : null}

      {wizard.currentStep === 0 ? (
        <AccountStep
          defaultValues={wizard.data}
          onSubmit={handleAccountSubmit}
          signupError={signupError}
          inProgress={inProgress}
          userTypes={userTypes}
          selectedUserType={userType}
          onUserTypeChange={onUserTypeChange}
          userTypeLocked={userTypeLocked}
        />
      ) : null}

      {customStepIndex >= 0 && customStepIndex < numCustomSteps ? (
        <CustomFieldsStep
          key={customStepIndex}
          fields={customFieldSteps[customStepIndex]}
          onSubmit={handleCustomFieldsSubmit}
          onSkip={handleSkip}
          onBack={handleBack}
          error={signupError}
          inProgress={inProgress}
        />
      ) : null}
    </div>
  );
};

export default SignupWizard;
